import EmptyPageContent from '@app/ccf/components/empty-page-content';
import { PageHeader } from '@app/components/layout/page/components/page-header';
import Page from '@app/components/layout/page/page';
import { useDataTableQueryState } from '@app/hooks/use-data-table-query-state';
import { useGetCompanyReportsList } from '@shared/api/hooks/ccf/reports';
import { CompanyReportStatusEnum } from '@shared/api/types';
import Text from '@shared/components/content/text';
import { Button } from '@shared/components/ui/button';
import { useEffect, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { ccfRoutes } from '../../ccf-routes';
import CCFReportTable from '../report/components/ccf-report-table';

export default function CCFListPage() {
  const navigate = useNavigate();
  const { page, perPage } = useDataTableQueryState();
  const [shouldPoll, setShouldPoll] = useState(true);

  const { data, isFetchedAfterMount, isLoading, isPreviousData, status } =
    useGetCompanyReportsList(
      {
        page: page,
        page_size: perPage,
      },
      {
        polling: shouldPoll,
        pollingInterval: 1000,
      }
    );

  // Disable polling if there are no publishing reports
  useEffect(() => {
    if (!isFetchedAfterMount) return;

    if (data && data.results.length > 0) {
      const hasPublishingReports = data.results.some(
        (report) => report.status === CompanyReportStatusEnum.publishing
      );
      if (!hasPublishingReports) setShouldPoll(false);
    }
  }, [data, isFetchedAfterMount]);

  return (
    <Page
      name="Company reports"
      flexFullHeight
      className="h-full"
      status={status}
    >
      <PageHeader
        name="Company reports"
        headingTestId="company-reports-heading"
        toolbar={
          data?.results.length ? (
            <Button
              type="button"
              data-testid="new-report-button"
              onClick={() =>
                navigate(generatePath(ccfRoutes.COMPANY_REPORT_SETUP_WELCOME))
              }
            >
              New report
            </Button>
          ) : undefined
        }
      />

      {data?.results.length === 0 ? (
        <EmptyPageContent
          title="Create your first company report"
          description={
            <div className="space-y-4">
              <Text className="text-center">
                A Company Carbon Footprint represents the total greenhouse gas
                emissions associated with your company&rsquo;s operations. This
                includes everything from energy and fuel use to supply chain
                activities and waste disposal, providing a full picture of your
                business&rsquo;s carbon impact.
              </Text>
              <Text className="text-center">
                You don&rsquo;t have any Company Carbon Footprint reports yet.
                Once you&rsquo;ve started your first report you&rsquo;ll see it
                listed here.
              </Text>
            </div>
          }
          primaryAction={() =>
            navigate(generatePath(ccfRoutes.COMPANY_REPORT_SETUP_WELCOME))
          }
          primaryActionText="Start your first report"
          primaryActionTestId="start-first-report-button"
          supportArticle="whatIsCCF"
        />
      ) : (
        <CCFReportTable
          type="card"
          className="flex-1"
          data={data}
          isFetchedAfterMount={isFetchedAfterMount}
          isLoading={isLoading}
          isPreviousData={isPreviousData}
        />
      )}
    </Page>
  );
}
